import Loading from 'components/common/loading/Loading'
import { favoritesCountBadgeClass, formatListingAddress } from 'components/myAccount/mySpaceUtils'
import { formatListingPrice } from 'components/propertyCard/formatListingPrice'
import { listingPublicAddressLine } from 'components/propertyCard/listingAddressLine'
import { Unit } from 'newTypes/unit'
import { forwardRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'
import { ListedProperty } from 'types/property'

export type OwnerListingFavoriteRow = {
  listing: Unit
  favoritesCount: number
}

type MySpaceFavoritesSectionProps = {
  isOwnerView: boolean
  savedFavorites: ListedProperty[]
  ownerListingFavorites: OwnerListingFavoriteRow[]
  isLoading: boolean
  onRemoveFavorite?: (propertyId: string) => void
  removingFavoriteId?: string | null
}

const HeartIcon = () => (
  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
    />
  </svg>
)

const MySpaceFavoritesSection = forwardRef<HTMLElement, MySpaceFavoritesSectionProps>(
  (
    {
      isOwnerView,
      savedFavorites,
      ownerListingFavorites,
      isLoading,
      onRemoveFavorite,
      removingFavoriteId,
    },
    ref
  ) => {
    const { t } = useTranslation()
    const navigate = useNavigate()

    const hasItems = isOwnerView
      ? ownerListingFavorites.length > 0
      : savedFavorites.length > 0

    return (
      <section
        ref={ref}
        id="my-space-favorites"
        className="scroll-mt-24 rounded-2xl border border-gray-100 bg-white p-5 shadow-sm sm:p-6"
      >
        {!isOwnerView ? (
          <Link
            to="/favorites"
            state={{ returnTo: '/account' }}
            className="group mb-4 flex items-center gap-3 rounded-xl transition-colors hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-imovPrimary"
            aria-label={t('account.mySpace.favoritesTitle')}
          >
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-imovPrimary/10 text-imovPrimary">
              <HeartIcon />
            </div>
            <div className="min-w-0 flex-1">
              <h2 className="text-lg font-semibold text-imovSecondary group-hover:text-imovPrimary">
                {t('account.mySpace.favoritesTitle')}
              </h2>
              <p className="text-sm text-gray-500">
                {t('account.mySpace.favoritesSubtitleSaved')}
              </p>
            </div>
          </Link>
        ) : (
          <div className="mb-4 flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-imovPrimary/10 text-imovPrimary">
              <HeartIcon />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-imovSecondary">
                {t('account.mySpace.favoritesTitle')}
              </h2>
              <p className="text-sm text-gray-500">
                {t('account.mySpace.favoritesSubtitleOwner')}
              </p>
            </div>
          </div>
        )}

        {isLoading ? (
          <Loading />
        ) : !hasItems ? (
          <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50 px-4 py-10 text-center">
            <p className="text-sm text-gray-600">{t('account.mySpace.noFavorites')}</p>
            {!isOwnerView ? (
              <button
                type="button"
                onClick={() => navigate('/listings')}
                className="mt-3 inline-flex rounded-xl bg-imovPrimary px-4 py-2 text-sm font-semibold text-white hover:bg-imovPrimaryDark"
              >
                {t('account.mySpace.browseListings')}
              </button>
            ) : null}
          </div>
        ) : isOwnerView ? (
          <ul className="space-y-3">
            {ownerListingFavorites.map(({ listing, favoritesCount }) => {
              const thumb = listing.images?.[0]
              const title = listing.title || t('account.mySpace.untitledListing')
              const address = formatListingAddress(listing)

              return (
                <li
                  key={listing.id}
                  className="flex flex-col gap-3 rounded-xl bg-gray-50 p-3 sm:flex-row sm:items-center sm:gap-4"
                >
                  <div className="flex min-w-0 flex-1 items-center gap-3">
                    <div className="h-16 w-16 shrink-0 overflow-hidden rounded-xl bg-gray-200">
                      {thumb ? (
                        <img
                          src={thumb}
                          alt=""
                          className="h-full w-full object-cover"
                          loading="lazy"
                        />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center text-xs text-gray-400">
                          —
                        </div>
                      )}
                    </div>
                    <div className="min-w-0">
                      <p className="truncate font-semibold text-imovSecondary">{title}</p>
                      {address ? (
                        <p className="mt-0.5 truncate text-sm text-gray-500">{address}</p>
                      ) : null}
                      <span className={`mt-2 ${favoritesCountBadgeClass}`}>
                        {t('account.mySpace.favoritesOnListing', { count: favoritesCount })}
                      </span>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      navigate(`/listing/${listing.id}`, {
                        state: { returnTo: '/account' },
                      })
                    }
                    className="shrink-0 rounded-xl border border-imovPrimary bg-white px-5 py-2 text-sm font-semibold text-imovPrimary hover:bg-imovPrimary/5"
                  >
                    {t('account.mySpace.seeListing')}
                  </button>
                </li>
              )
            })}
          </ul>
        ) : (
          <ul className="space-y-3">
            {savedFavorites.map(property => {
              const thumb = property.images?.[0]
              const location = listingPublicAddressLine(property) || '—'
              const title =
                (property as ListedProperty & { title?: string }).title ||
                location ||
                t('account.mySpace.untitledListing')
              const price = formatListingPrice(property.price, property.currency)

              return (
                <li
                  key={property.id}
                  className="flex flex-col gap-3 rounded-xl bg-gray-50 p-3 sm:flex-row sm:items-center sm:gap-4"
                >
                  <div className="flex min-w-0 flex-1 items-center gap-3">
                    <div className="h-16 w-16 shrink-0 overflow-hidden rounded-xl bg-gray-200">
                      {thumb ? (
                        <img
                          src={thumb}
                          alt=""
                          className="h-full w-full object-cover"
                          loading="lazy"
                        />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center text-xs text-gray-400">
                          —
                        </div>
                      )}
                    </div>
                    <div className="min-w-0">
                      <p className="truncate font-semibold text-imovSecondary">{title}</p>
                      <p className="mt-0.5 truncate text-sm text-gray-500">{location}</p>
                      <p className="mt-1 text-sm font-medium text-imovSecondary">
                        {price}
                      </p>
                    </div>
                  </div>
                  <div className="flex shrink-0 flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => onRemoveFavorite?.(property.id)}
                      disabled={removingFavoriteId === property.id}
                      className="rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                    >
                      {t('account.mySpace.removeFavorite')}
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        navigate(`/listing/${property.id}`, {
                          state: { returnTo: '/account' },
                        })
                      }
                      className="rounded-xl border border-imovPrimary bg-white px-5 py-2 text-sm font-semibold text-imovPrimary hover:bg-imovPrimary/5"
                    >
                      {t('account.mySpace.seeListing')}
                    </button>
                  </div>
                </li>
              )
            })}
          </ul>
        )}
      </section>
    )
  }
)

MySpaceFavoritesSection.displayName = 'MySpaceFavoritesSection'

export default MySpaceFavoritesSection
